import axios from "@/utils/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@elaniintc/goes-design-system";

// ---------------------------------------------------------------------------
// Dashboard — Unlink association (JER-453)
// POST /admin/v1/associations/{id}/unlink
// ---------------------------------------------------------------------------

interface UnlinkAssociationPayload {
  id: string;
  reason?: string;
}

export const useUnlinkAssociation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, reason }: UnlinkAssociationPayload) => {
      const response = await axios.post(`/admin/v1/associations/${id}/unlink`, {
        ...(reason ? { reason } : {}),
      });
      return response.data;
    },
    onSuccess: () => {
      toast.showToast({
        title: "Usuario desvinculado",
        description:
          "El usuario fue desvinculado, ahora ya no tendrá acceso a los datos de la representación.",
        options: { type: "success", duration: 5000 },
      });
      queryClient.invalidateQueries({ queryKey: ["user-associations"] });
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;

      // 409: the association is no longer active (already unlinked, expired…).
      if (status === 409) {
        toast.showToast({
          title: "Las partes ya no están vinculadas",
          description: "Esta asociación ya no está activa.",
          options: { type: "error", duration: 5000 },
        });
        queryClient.invalidateQueries({ queryKey: ["user-associations"] });
        return;
      }

      toast.showToast({
        title: "Error al intentar desvincular usuario",
        description:
          "El usuario no pudo ser desvinculado. Por favor, inténtelo de nuevo.",
        options: { type: "error", duration: 5000 },
      });
    },
  });
};
